import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import api from '../api/client';
import AuthHero from '../components/AuthHero';

/* تعیینِ رمز (اولین ورود/توکنِ کوتاه‌عمر) یا تغییرِ رمز (کاربرِ واردشده).
   الزاماتِ افتا زنده ارزیابی می‌شوند: تیک/رنگ، شمارندهٔ کاراکتر و نوارِ قدرت. */

const toEnglishDigits = (v) =>
  String(v || '').replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d));

const MIN_LEN = 10;
const toFa = (v) => String(v).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #eef2ff 0%, #f8fafc 60%, #e0f2fe 100%)',
    direction: 'rtl',
  },
  card: { maxWidth: 500, width: '100%', borderRadius: 18 },
  content: { padding: theme.spacing(4) },
  title: { fontWeight: 800, color: '#1a237e', marginBottom: theme.spacing(1) },
  hint: { color: '#4a5578', lineHeight: 2, marginBottom: theme.spacing(2) },
  field: { marginBottom: theme.spacing(1.5) },
  row: { display: 'flex', alignItems: 'flex-start', gap: 6 },
  eye: {
    marginTop: 6,
    color: '#64748b',
    background: 'transparent',
    border: 0,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 10,
  },
  rules: {
    background: '#f8fbfc',
    border: '1px solid #e2eef1',
    borderRadius: 14,
    padding: '12px 14px',
    marginBottom: theme.spacing(2),
  },
  ruleList: { listStyle: 'none', padding: 0, margin: 0 },
  rule: { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, lineHeight: 2, color: '#64748b' },
  ruleOk: { color: '#065f46', fontWeight: 800 },
  mark: { width: 16, textAlign: 'center' },
  bar: { height: 6, background: '#eef2f7', borderRadius: 999, overflow: 'hidden', marginTop: 10 },
  barFill: { display: 'block', height: '100%', transition: 'width .2s' },
  barLabel: { fontSize: 11.5, marginTop: 5 },
  btn: { marginTop: theme.spacing(1) },
}));

function evaluate(password) {
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const cats = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  const ascii = password.length > 0 && /^[\x20-\x7E]*$/.test(password);
  return { cats, ascii };
}

export default function SetPassword() {
  const classes = useStyles();
  const [mode, setMode] = useState('setup');
  const [current, setCurrent] = useState('');
  const [pwd, setPwd] = useState({ password: '', confirm: '' });
  const [show, setShow] = useState({ current: false, password: false, confirm: false });
  const [capsOn, setCapsOn] = useState(false);
  const [langWarn, setLangWarn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let token = '';
    try { token = localStorage.getItem('setup_token') || ''; } catch (e) { /* noop */ }
    setMode(token ? 'setup' : 'change');
  }, []);

  const { cats, ascii } = useMemo(() => evaluate(pwd.password), [pwd.password]);
  const lenOk = pwd.password.length >= MIN_LEN;
  const matchOk = pwd.password.length > 0 && pwd.password === pwd.confirm;
  const score = Math.min(pwd.password.length, 20) + cats * 6 + (ascii ? 3 : 0) + (lenOk ? 6 : 0);
  const pct = Math.max(0, Math.min(100, Math.round((score / 47) * 100)));
  const strength = !pwd.password.length
    ? { text: '—', color: '#e5e7eb', label: '#64748b' }
    : pct < 45
      ? { text: 'ضعیف', color: '#dc2626', label: '#b91c1c' }
      : pct < 75
        ? { text: 'متوسط', color: '#f59e0b', label: '#b45309' }
        : { text: 'قوی', color: '#059669', label: '#047857' };

  const filterAscii = (value) => String(value || '').replace(/[^\x20-\x7E]/g, '');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.password !== pwd.confirm) {
      setError('تکرارِ رمز یکسان نیست');
      return;
    }
    const setup = (() => {
      try { return localStorage.getItem('setup_token') || ''; } catch (x) { return ''; }
    })();
    if (mode === 'setup' && !setup) {
      setError('مهلتِ تعیینِ رمز تمام شده است؛ دوباره وارد شوید.');
      return;
    }
    setLoading(true);
    try {
      if (setup) {
        api.defaults.headers.common.Authorization = `Bearer ${setup}`;
      }
      const payload = { password: toEnglishDigits(pwd.password), password_confirm: toEnglishDigits(pwd.confirm) };
      if (mode === 'change') {
        payload.current_password = toEnglishDigits(current);
      }
      const res = await api.post('users/password/set/', payload);
      if (res.data.tokens) {
        localStorage.setItem('access', res.data.tokens.access);
        localStorage.setItem('refresh', res.data.tokens.refresh);
        api.defaults.headers.common.Authorization = `Bearer ${res.data.tokens.access}`;
      }
      localStorage.removeItem('setup_token');
      window.location.assign('/');
    } catch (err) {
      const data = err.response && err.response.data;
      setError((data && (data.error || data.detail)) || 'تعیینِ رمز ممکن نشد');
    } finally {
      setLoading(false);
    }
  };

  const rule = (ok, text, extra = '') => (
    <li className={`${classes.rule} ${ok ? classes.ruleOk : ''}`}>
      <span className={classes.mark}>{ok ? '✔' : '○'}</span>
      <span>{text}{extra}</span>
    </li>
  );

  return (
    <AuthHero
      title={mode === 'setup' ? 'تعیینِ رمزِ عبور' : 'تغییرِ رمزِ عبور'}
      subtitle="برای امنیت و محرمانگیِ اطلاعاتِ خودتان، رمزی انتخاب کنید که حداقلِ الزاماتِ افتا را داشته باشد."
    >
            {error && (
              <MuiAlert severity="error" style={{ marginBottom: 16 }} onClose={() => setError('')}>
                {error}
              </MuiAlert>
            )}
            <form onSubmit={submit}>
              {mode === 'change' && (
                <div className={classes.row}>
                  <TextField
                    fullWidth variant="outlined" type={show.current ? 'text' : 'password'}
                    label="رمزِ فعلی" className={classes.field} value={current} dir="ltr"
                    inputProps={{ style: { fontFamily: 'monospace', textAlign: 'left' } }}
                    onChange={(e) => { const raw = e.target.value; setLangWarn(/[^\\x20-\\x7E]/.test(raw)); setCurrent(filterAscii(raw)); }}
                    onKeyDown={(e) => setCapsOn(!!(e.getModifierState && e.getModifierState('CapsLock')))}
                    required
                  />
                  <button type="button" className={classes.eye}
                    onClick={() => setShow((s) => ({ ...s, current: !s.current }))}>
                    {show.current ? <VisibilityOff /> : <Visibility />}
                  </button>
                </div>
              )}
              <div className={classes.row}>
                <TextField
                  fullWidth variant="outlined" type={show.password ? 'text' : 'password'}
                  label="رمزِ جدید" className={classes.field} value={pwd.password} dir="ltr"
                  inputProps={{ style: { fontFamily: 'monospace', textAlign: 'left' }, minLength: MIN_LEN }}
                  onChange={(e) => { const raw = e.target.value; setLangWarn(/[^\\x20-\\x7E]/.test(raw)); setPwd((p) => ({ ...p, password: filterAscii(raw) })); }}
                  onKeyDown={(e) => setCapsOn(!!(e.getModifierState && e.getModifierState('CapsLock')))}
                  required
                />
                <button type="button" className={classes.eye}
                  onClick={() => setShow((s) => ({ ...s, password: !s.password }))}>
                  {show.password ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              <div className={classes.row}>
                <TextField
                  fullWidth variant="outlined" type={show.confirm ? 'text' : 'password'}
                  label="تکرارِ رمز" className={classes.field} value={pwd.confirm} dir="ltr"
                  inputProps={{ style: { fontFamily: 'monospace', textAlign: 'left' }, minLength: MIN_LEN }}
                  onChange={(e) => { const raw = e.target.value; setLangWarn(/[^\\x20-\\x7E]/.test(raw)); setPwd((p) => ({ ...p, confirm: filterAscii(raw) })); }}
                  onKeyDown={(e) => setCapsOn(!!(e.getModifierState && e.getModifierState('CapsLock')))}
                  required
                />
                <button type="button" className={classes.eye}
                  onClick={() => setShow((s) => ({ ...s, confirm: !s.confirm }))}>
                  {show.confirm ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>

              {langWarn && (
                <MuiAlert severity="warning" style={{ marginBottom: 12 }}>
                  صفحه‌کلید را به انگلیسی تغییر دهید؛ رمز فقط با حروف و ارقامِ انگلیسی پذیرفته می‌شود.
                </MuiAlert>
              )}
              {capsOn && (
                <MuiAlert severity="warning" style={{ marginBottom: 12 }}>
                  CapsLock روشن است؛ برای امنیتِ رمز آن را خاموش کنید.
                </MuiAlert>
              )}

              <div className={classes.rules}>
                <Typography variant="caption" style={{ color: '#475569', lineHeight: 1.9 }}>
                  رمز شما طبق <b>الزاماتِ افتا</b> ارزیابی می‌شود؛ هر مورد که رعایت شود سبز و تیک‌دار می‌شود.
                </Typography>
                <ul className={classes.ruleList}>
                  {rule(lenOk, `حداقل ${toFa(MIN_LEN)} کاراکتر`, ` (شما ${toFa(pwd.password.length)} کاراکتر)`)}
                  {rule(cats >= 3, 'حداقل سه دسته از حروفِ کوچک، بزرگ، رقم و نماد', ` — ${toFa(cats)} از ۴`)}
                  {rule(ascii, 'فقط حروف و ارقامِ انگلیسی (صفحه‌کلید انگلیسی)')}
                  {rule(matchOk, 'تکرارِ رمز با رمزِ اصلی یکسان باشد')}
                  <li className={classes.rule}>
                    <span className={classes.mark}>○</span>
                    <span>از رمزهای رایج نباشد (هنگام ثبت بررسی می‌شود)</span>
                  </li>
                  <li className={classes.rule}>
                    <span className={classes.mark}>○</span>
                    <span>با سه رمزِ آخرِ شما یکسان نباشد (هنگام ثبت بررسی می‌شود)</span>
                  </li>
                </ul>
                <div className={classes.bar}>
                  <span className={classes.barFill} style={{ width: `${pct}%`, background: strength.color }} />
                </div>
                <div className={classes.barLabel} style={{ color: strength.label }}>
                  قدرتِ رمز: {strength.text}
                </div>
              </div>

              <Button type="submit" fullWidth color="primary" variant="contained"
                className={classes.btn} disabled={loading}>
                {loading ? <CircularProgress size={22} /> : (mode === 'setup' ? 'ثبتِ رمز و ورود' : 'تغییرِ رمز')}
              </Button>
            </form>
            <Box mt={2} textAlign="center">
              <Typography variant="body2" style={{ color: '#1565c0', cursor: 'pointer' }}
                onClick={() => window.location.assign('/login')}>
                بازگشت
              </Typography>
            </Box>
    </AuthHero>
  );
}
